"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/components/ui/use-toast";
import {
  MapPin,
  Clock,
  Calendar,
  LogIn,
  LogOut,
  Briefcase,
  AlertCircle,
  Info,
  Sun,
  Moon,
  Fingerprint,
  CheckCircle,
  X,
  Clock4,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import { useAppContext } from "@/context/AppContext";
import Webcam from "react-webcam";

const API_BASE =  process.env.NEXT_PUBLIC_API_URL || "https://checkinoutpro.com";

//const API_BASE = process.env.NEXT_PUBLIC_API_URL || "http://localhost:4000";

// helpers 
const toRad = (x: number) => (x * Math.PI) / 180;
const haversineMeters = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
) => {
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};
const to24h = (t?: string) => {
  const s = (t || "").trim();
  if (!s) return "00:00";
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [h, m] = s.split(":").map(Number);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }
  const m = s.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (!m) return "00:00";
  let hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const ap = m[3].toUpperCase();
  if (hh === 12) hh = ap === "PM" ? 12 : 0;
  else if (ap === "PM") hh += 12;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};
const withinWindow = (start?: string, end?: string, now = new Date()) => {
  const [sh, sm] = to24h(start).split(":").map(Number);
  const [eh, em] = to24h(end).split(":").map(Number);
  const cur = now.getHours() * 60 + now.getMinutes();
  const s = sh * 60 + sm;
  const e = eh * 60 + em;
  return e >= s ? cur >= s && cur <= e : cur >= s || cur <= e;
};

type SiteLike = {
  _id?: string | number;
  id?: string | number;
  name?: string;
  projectNumber?: string;
  latitude?: number | string;
  longitude?: number | string;
  lat?: number | string;
  lng?: number | string;
  location?: {
    lat?: number | string;
    lng?: number | string;
    latitude?: number | string;
    longitude?: number | string;
  };
  radius?: number | string;
  geofenceRadius?: number | string;
  geoRadius?: number | string;
  startTime?: string;
  endTime?: string;
};

function getSiteGeo(
  site: SiteLike,
): { lat: number; lng: number; radius: number } | null {
  const rawLat =
    site?.latitude ??
    site?.lat ??
    site?.location?.lat ??
    site?.location?.latitude;
  const rawLng =
    site?.longitude ??
    site?.lng ??
    site?.location?.lng ??
    site?.location?.longitude;
  const rawRad = site?.radius ?? site?.geofenceRadius ?? site?.geoRadius;
  const lat = typeof rawLat === "string" ? parseFloat(rawLat) : rawLat;
  const lng = typeof rawLng === "string" ? parseFloat(rawLng) : rawLng;
  const radius = typeof rawRad === "string" ? parseFloat(rawRad) : rawRad;
  if (typeof lat !== "number" || !Number.isFinite(lat) || lat < -90 || lat > 90)
    return null;
  if (
    typeof lng !== "number" ||
    !Number.isFinite(lng) ||
    lng < -180 ||
    lng > 180
  )
    return null;
  if (lat === 0 && lng === 0) return null;
  return {
    lat,
    lng,
    radius:
      typeof radius === "number" && Number.isFinite(radius) && radius > 0
        ? radius
        : 0,
  };
}

const idToString = (val: any) =>
  typeof val === "string"
    ? val
    : val?.$oid ||
      val?._id ||
      (typeof val === "object" && "toString" in val ? String(val) : "");

// Promisified GPS
function waitForGPS(options?: PositionOptions): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      return reject(new Error("Geolocation not supported in this browser."));
    }

    let timeoutId: NodeJS.Timeout;

    const onSuccess = (pos: GeolocationPosition) => {
      clearTimeout(timeoutId);
      resolve(pos);
    };

    const onError = (err: GeolocationPositionError) => {
      clearTimeout(timeoutId);
      let msg = "Unable to get your location. ";
      if (err.code === 1) msg += "Permission denied.";
      else if (err.code === 2) msg += "Position unavailable.";
      else if (err.code === 3) msg += "Request timed out.";
      reject(new Error(msg));
    };

    timeoutId = setTimeout(() => {
      reject(new Error("GPS request timed out after 15 seconds."));
    }, 15000);

    navigator.geolocation.getCurrentPosition(
      onSuccess,
      onError,
      options ?? { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
}

/* persistence helpers (for refresh survival)  */
const LS_KEYS = {
  token: "token",
  worker: "worker",
  isCheckedIn: "wp_isCheckedIn",
  checkInISO: "wp_checkInISO",
  companyName: "wp_companyName",
  selectedSiteId: "wp_selectedSiteId",
};

function saveSession(partial: Record<string, any>) {
  Object.entries(partial).forEach(([k, v]) => {
    try {
      if (v === undefined || v === null) return;
      if (typeof v === "string") localStorage.setItem(k, v);
      else localStorage.setItem(k, JSON.stringify(v));
    } catch {}
  });
}

function loadJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function clearSession() {
  try {
    Object.values(LS_KEYS).forEach((k) => localStorage.removeItem(k));
  } catch {}
}

/*  component  */
export default function WorkerPortal() {
  const {
    attendance,
    addAttendanceRecord,
    theme,
    setTheme,
    checkInWorker,
    checkOutWorker,
  } = useAppContext();
  const { toast } = useToast();

  const webcamRef = useRef<Webcam>(null);

  // auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [worker, setWorker] = useState<any>(null);

  //  Biometric setup state
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [faceData, setFaceData] = useState<number[]>([]);
  const [fingerData, setFingerData] = useState<string>("");

  const [isScanningFingerprint, setIsScanningFingerprint] = useState(false);
  const [fingerProgress, setFingerProgress] = useState(0);

  // login inputs (username = employeeNumber, password = loginId)
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [password, setPassword] = useState("");

  // company + sites
  const [companyName, setCompanyName] = useState("");
  const [companySites, setCompanySites] = useState<any[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState<string>("");

  // session/UX
  // const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [isCheckedIn, setIsCheckedIn] = useState(false);

  const [syncingStatus, setSyncingStatus] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);

  const [checkInTime, setCheckInTime] = useState<Date | null>(null);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // GPS only
  const [location, setLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [locationError, setLocationError] = useState("");

  // auth dialog
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [processingAuth, setProcessingAuth] = useState(false);
  const [authProgress, setAuthProgress] = useState(0);
  const [authStatus, setAuthStatus] = useState("");
  const [authSuccess, setAuthSuccess] = useState<boolean | null>(null);

  // placeholders (UI only)
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fingerprintRef = useRef<HTMLDivElement | null>(null);
  const chatRef = useRef<HTMLDivElement | null>(null);

  // Time setting
  const [timeSettings, setTimeSettings] = useState<{
    onTimeBefore?: string;
    lateUntil?: string;
    absentAfter?: string;
    endTime?: string;
  } | null>(null);
  const [settingsLoading, setSettingsLoading] = useState<boolean>(false);
  const [settingsError, setSettingsError] = useState<string>("");

  useEffect(() => {
    if (!worker?._id) return;

    const checkCurrentStatus = async () => {
      setStatusLoading(true);

      try {
        const token = localStorage.getItem(LS_KEYS.token) || "";
        const res = await fetch(
          `${API_BASE}/api/attendance/worker/${worker._id}/status`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();

        setIsCheckedIn(data?.status === "checked_in");
      } catch (err) {
        setIsCheckedIn(false);
      } finally {
        setStatusLoading(false);
      }
    };

    checkCurrentStatus();
  }, [worker]);

  useEffect(() => {
    const loadModels = async () => {
      const faceapi = await import("@vladmandic/face-api");
      await faceapi.nets.tinyFaceDetector.loadFromUri("/models");
      await faceapi.nets.faceLandmark68Net.loadFromUri("/models");
      await faceapi.nets.faceRecognitionNet.loadFromUri("/models");
    };

    loadModels();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    const startDetection = async () => {
      if (!showAuthDialog || !webcamRef.current || !canvasRef.current) return;

      const faceapi = await import("@vladmandic/face-api");
      const video = webcamRef.current.video as HTMLVideoElement;
      const canvas = canvasRef.current;

      const displaySize = {
        width: video.videoWidth,
        height: video.videoHeight,
      };
      faceapi.matchDimensions(canvas, displaySize);

      interval = setInterval(async () => {
        if (!video.paused && !video.ended) {
          const detections = await faceapi
            .detectAllFaces(video, new faceapi.TinyFaceDetectorOptions())
            .withFaceLandmarks();

          const resizedDetections = faceapi.resizeResults(
            detections,
            displaySize,
          );
          const context = canvas.getContext("2d");
          context?.clearRect(0, 0, canvas.width, canvas.height);
          faceapi.draw.drawDetections(canvas, resizedDetections);
          faceapi.draw.drawFaceLandmarks(canvas, resizedDetections);
        }
      }, 200);
    };

    startDetection();

    return () => clearInterval(interval);
  }, [showAuthDialog]);

  useEffect(() => {
    if (!worker) return;

    const fetchSettings = async () => {
      setSettingsLoading(true);
      setSettingsError("");
      try {
        const token = localStorage.getItem(LS_KEYS.token) || "";
        const res = await fetch(`${API_BASE}/api/time-settings`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || "Failed to load time settings");
        }
        const json = await res.json();
        setTimeSettings(json.data || {});
      } catch (e: any) {
        setSettingsError(e.message);
      } finally {
        setSettingsLoading(false);
      }
    };

    fetchSettings();
  }, [worker]);

  useEffect(() => {
    if (showBiometricSetup && videoRef.current) {
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
          }
        })
        .catch((err) => {
          toast({
            title: "Camera error",
            description: "Please allow access to your webcam.",
            variant: "destructive",
          });
        });
    } else if (!showBiometricSetup && videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((t) => t.stop());
      videoRef.current.srcObject = null;
    }
  }, [showBiometricSetup]);

  function stopCamera() {
    try {
      // Stop videoRef (used in validation dialog)
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach((t) => t.stop());
        videoRef.current.pause();
        videoRef.current.srcObject = null;
        videoRef.current.load();
      }

      // Stop webcamRef (used in biometric setup)
      if (webcamRef.current && webcamRef.current.stream) {
        const stream = webcamRef.current.stream as MediaStream;
        stream.getTracks().forEach((t) => t.stop());
        webcamRef.current.video?.pause();
        webcamRef.current.video?.removeAttribute("srcObject");
        webcamRef.current.video!.srcObject = null;
        webcamRef.current.video?.load();
      }

      // Defensive: kill *any* active camera stream from other sources
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((s) => s.getTracks().forEach((t) => t.stop()))
        .catch(() => {});
    } catch (err) {}
  }

  useEffect(() => {
    if (!showAuthDialog || !isLoggedIn) {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [showAuthDialog, isLoggedIn]);

  // auto-select the first site if none is chosen
  useEffect(() => {
    if (isLoggedIn && companySites?.length > 0 && !selectedSiteId) {
      setSelectedSiteId(String(companySites[0]._id ?? companySites[0].id));
      localStorage.setItem(
        LS_KEYS.selectedSiteId,
        String(companySites[0]._id ?? companySites[0].id),
      );
    }
  }, [isLoggedIn, companySites, selectedSiteId]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // infer open session from recentActivity (first load after login)
  useEffect(() => {
    if (!worker || recentActivity.length === 0) return;
    const open = [...recentActivity]
      .sort(
        (a, b) =>
          new Date(`${b.date} ${b.time}`).getTime() -
          new Date(`${a.date} ${a.time}`).getTime(),
      )
      .find(
        (act) =>
          act.type === "check-in" &&
          !recentActivity.some(
            (x) => x.type === "check-out" && x.date === act.date,
          ),
      );
    if (open) {
      setIsCheckedIn(true);
      setCheckInTime(new Date(`${open.date} ${open.time}`));
    }
  }, [worker, recentActivity]);

  useEffect(() => {
    if (!isCheckedIn || !checkInTime) return;
    const t = setInterval(
      () => setCheckInTime((p) => (p ? new Date(p.getTime()) : p)),
      1000,
    );
    return () => clearInterval(t);
  }, [isCheckedIn, checkInTime]);

  // quick GPS read (used on login & when opening the dialog)
  const getCurrentLocation = () => {
    setLocationError("");
    if (!navigator.geolocation) {
      setLocation(null);
      setLocationError("Geolocation is not supported by your browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
      (err) => {
        let msg = "Unable to get your location. ";
        if (err.code === 1) msg += "Location access was denied.";
        else if (err.code === 2) msg += "Location information is unavailable.";
        else if (err.code === 3) msg += "The request timed out.";
        else msg += "Unknown error.";
        setLocation(null);
        setLocationError(msg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  // sites loader
  const loadSitesForCompany = async (companyIdStr: string, token?: string) => {
    setSitesLoading(true);
    const tryFetch = async (url: string, withAuth: boolean) => {
      const headers: Record<string, string> = {};
      const tk = token || localStorage.getItem(LS_KEYS.token) || "";
      if (withAuth && tk) headers.Authorization = `Bearer ${tk}`;
      const res = await fetch(url, {
        method: "GET",
        headers,
        credentials: "include",
        mode: "cors",
      });
      if (!res.ok) {
        console.warn(
          "[sites] GET",
          url,
          res.status,
          await res.text().catch(() => ""),
        );
        return null;
      }
      try {
        const json = await res.json();
        return Array.isArray(json)
          ? json
          : json?.data && Array.isArray(json.data)
            ? json.data
            : [];
      } catch {
        return [];
      }
    };

    let arr =
      (await tryFetch(
        `${API_BASE}/api/sites/public/company/${companyIdStr}`,
        false,
      )) ??
      (await tryFetch(
        `${API_BASE}/api/sites/public/company/${companyIdStr}`,
        true,
      ));
    if (arr && arr.length) {
      setCompanySites(arr);
      setSitesLoading(false);
      return;
    }

    arr =
      (await tryFetch(`${API_BASE}/api/sites/company/${companyIdStr}`, true)) ??
      (await tryFetch(`${API_BASE}/api/sites/company/${companyIdStr}`, false));
    if (arr && arr.length) {
      setCompanySites(arr);
      setSitesLoading(false);
      return;
    }

    arr =
      (await tryFetch(
        `${API_BASE}/api/companies/${companyIdStr}/sites`,
        true,
      )) ??
      (await tryFetch(
        `${API_BASE}/api/companies/${companyIdStr}/sites`,
        false,
      ));
    if (arr && arr.length) {
      setCompanySites(arr);
      setSitesLoading(false);
      return;
    }

    arr =
      (await tryFetch(
        `${API_BASE}/api/sites?companyId=${encodeURIComponent(companyIdStr)}`,
        true,
      )) ??
      (await tryFetch(
        `${API_BASE}/api/sites?companyId=${encodeURIComponent(companyIdStr)}`,
        false,
      ));
    if (arr && arr.length) {
      setCompanySites(arr);
      setSitesLoading(false);
      return;
    }

    const all =
      (await tryFetch(`${API_BASE}/api/sites`, true)) ??
      (await tryFetch(`${API_BASE}/api/sites`, false)) ??
      [];
    setCompanySites(
      all.filter((s: any) => idToString(s.companyId) === companyIdStr),
    );
    setSitesLoading(false);
  };

  useEffect(() => {
    if (!showBiometricSetup && webcamRef.current?.stream) {
      const tracks = webcamRef.current.stream.getTracks();
      tracks.forEach((t) => t.stop());
    }
  }, [showBiometricSetup]);

  //REHYDRATE ON MOUNT (survive refresh)

  useEffect(() => {
    const initPortal = async () => {
      try {
        const token = localStorage.getItem(LS_KEYS.token);
        const storedWorker = loadJSON<any>(LS_KEYS.worker, null);
        const storedCompany = localStorage.getItem(LS_KEYS.companyName) || "";
        const storedSelectedSiteId =
          localStorage.getItem(LS_KEYS.selectedSiteId) || "";

        // Get requirement as a boolean
        const isBioRequired =
          localStorage.getItem("requireBiometric") !== "false";

        if (!token || !storedWorker) {
          setSyncingStatus(false);
          return;
        }

        // Set all states together
        setWorker(storedWorker);
        setCompanyName(storedCompany);
        setSelectedSiteId(storedSelectedSiteId);
        setIsLoggedIn(true);

        // ONLY SHOW SETUP IF REQUIRED AND MISSING DATA
        if (
          isBioRequired &&
          !storedWorker.hasPhoto &&
          !storedWorker.hasFingerprint
        ) {
          setShowBiometricSetup(true);
        } else {
          setShowBiometricSetup(false);
        }

        // Check current check-in status from server
        const wid = String(storedWorker._id ?? storedWorker.id ?? "");
        const res = await fetch(
          `${API_BASE}/api/attendance/worker/${wid}/status`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (res.ok) {
          const json = await res.json();
          const isIn = json?.status === "checked_in";
          setIsCheckedIn(isIn);
          if (isIn) {
            setCheckInTime(new Date(json.lastCheckInTime || Date.now()));
          }
        }

        const companyIdStr = idToString(storedWorker.companyId);
        if (companyIdStr) await loadSitesForCompany(companyIdStr, token);
      } catch (err) {
        console.error("Init error", err);
      } finally {
        setSyncingStatus(false);
      }
    };

    initPortal();
  }, []);

  const handleLogin = async () => {
    setIsLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/workers/authenticate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          loginId: employeeNumber,
          password,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error((await res.text()) || "Invalid credentials");

      const token = data.token || data.accessToken || "";
      const found = data.worker || data.user || data;

      // Extract the company preference from the API response
      const isBioRequired = data.company?.requireBiometricRegistration ?? true;

      if (token) localStorage.setItem(LS_KEYS.token, token);

      // Save core worker data only (no isCheckedIn yet)
      saveSession({
        [LS_KEYS.worker]: found,
        [LS_KEYS.companyName]: data.company?.name || found.companyName || "",
        requireBiometric: String(isBioRequired),
      });

      setWorker(found);
      setCompanyName(data.company?.name || found.companyName || "");

      const companyIdStr = idToString(found.companyId);
      if (companyIdStr) await loadSitesForCompany(companyIdStr, token);

      try {
        const statusRes = await fetch(
          `${API_BASE}/api/attendance/status?workerId=${found._id || found.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (statusRes.ok) {
          const statusJson = await statusRes.json();
          if (statusJson.isCheckedIn) {
            setIsCheckedIn(true);
            const checkInTime = new Date(
              statusJson.lastCheckInTime || Date.now(),
            );
            setCheckInTime(checkInTime);
            saveSession({
              [LS_KEYS.isCheckedIn]: "true",
              [LS_KEYS.checkInISO]: checkInTime.toISOString(),
            });
          } else {
            setIsCheckedIn(false);
            saveSession({
              [LS_KEYS.isCheckedIn]: "false",
              [LS_KEYS.checkInISO]: "",
            });
          }
        }
      } catch (err) {}

      setIsLoggedIn(true);

      // Only show setup if COMPANY REQUIRES IT AND WORKER HAS NO DATA
      if (isBioRequired === true && !found.hasPhoto && !found.hasFingerprint) {
        setShowBiometricSetup(true);
      } else {
        setShowBiometricSetup(false);
      }

      toast({
        title: "Login Successful",
        description: `Welcome back, ${found.fullName || found.employeeNumber}!`,
        variant: "success",
      });
    } catch (e: any) {
      setError(typeof e?.message === "string" ? e.message : "Login failed");
      toast({
        title: "Invalid credentials",
        description: "Check your Employee ID and Login ID",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openValidationDialog = async () => {
    const isBioRequiredSetting =
      localStorage.getItem("requireBiometric") !== "false";

    // Check for site selection first
    if (!selectedSiteId && companySites.length > 0) {
      const first = String(companySites[0]._id ?? companySites[0].id);
      setSelectedSiteId(first);
      localStorage.setItem(LS_KEYS.selectedSiteId, first);
    }

    if (!selectedSiteId && companySites.length === 0) {
      toast({
        title: "Site required",
        description: "No sites available.",
        variant: "destructive",
      });
      return;
    }

    // THE AUTO-VERIFY SHORTCUT
    if (!isBioRequiredSetting) {
      // Show the dialog but keep it on the "Processing" state immediately
      setShowAuthDialog(true);
      // Trigger the recording logic directly
      handleValidateAndRecord();
      return;
    }

    //STANDARD FLOW (If Biometrics are ON)
    getCurrentLocation();
    setShowAuthDialog(true);

    setTimeout(() => {
      if (videoRef.current) {
        navigator.mediaDevices
          .getUserMedia({ video: { facingMode: "user" } })
          .then((stream) => {
            videoRef.current!.srcObject = stream;
            return videoRef.current!.play();
          })
          .catch((err) => {
            toast({ title: "Camera error", variant: "destructive" });
          });
      }
    }, 300);
  };

  // fallback direct POST if context funcs are no-op
  async function fallbackAttendance(action: "in" | "out", payload: any) {
    const token = localStorage.getItem(LS_KEYS.token) || "";
    const url =
      action === "in"
        ? `${API_BASE}/api/attendance/check-in`
        : `${API_BASE}/api/attendance/check-out`;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      let data: any = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (!res.ok) {
        const msg = data.message || data.error || "";
        if (msg.includes("already checked in")) {
          throw new Error("already-checked-in");
        }
        if (msg.includes("already checked out")) {
          throw new Error("already-checked-out");
        }
        throw new Error(msg || "Server error");
      }

      return data || {};
    } catch (err) {
      return {};
    }
  }

  const handleValidateAndRecord = async () => {
    setProcessingAuth(true);
    setAuthProgress(5);
    setAuthStatus("Starting validation...");
    setAuthSuccess(null);

    try {
      if (!worker) throw new Error("Please login first.");

      const isBioRequiredSetting =
        localStorage.getItem("requireBiometric") !== "false";
      let faceDescriptorToSubmit: number[] = [];

      // BIOMETRIC LOGIC
      if (isBioRequiredSetting) {
        setAuthStatus("Scanning face...");
        const video = videoRef.current as HTMLVideoElement;
        if (!video || video.readyState < 2)
          throw new Error("Camera not ready.");

        const faceapi = await import("@vladmandic/face-api");
        const detection = await faceapi
          .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks()
          .withFaceDescriptor();

        if (!detection) throw new Error("No face detected. Please try again.");
        faceDescriptorToSubmit = Array.from(detection.descriptor);
      }

      // GPS & RADIUS LOGIC (MODIFIED TO ALLOW MISMATCH)
      setAuthProgress(40);
      setAuthStatus("Verifying Location...");
      const pos = await waitForGPS();
      const curLoc = {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
      };
      setLocation(curLoc);

      const site = companySites.find(
        (s: any) => String(s._id ?? s.id) === selectedSiteId,
      );
      const geo = getSiteGeo(site!);

      let siteMatched = true;
      let distanceMeters = 0;

      if (geo) {
        distanceMeters = haversineMeters(
          curLoc.latitude,
          curLoc.longitude,
          geo.lat,
          geo.lng,
        );

        // CHECK RADIUS: If outside, we mark as false but DO NOT throw error
        if (geo.radius > 0 && distanceMeters > geo.radius) {
          siteMatched = false;
        }
      }

      // SUBMISSION LOGIC
      setAuthProgress(80);
      // Visual feedback for the worker
      setAuthStatus(
        siteMatched
          ? "Recording Attendance..."
          : "Recording (Location Mismatch)...",
      );

      const apiResp = await fallbackAttendance(isCheckedIn ? "out" : "in", {
        workerId: String(worker._id ?? worker.id),
        siteId: selectedSiteId,
        siteLocation: geo ? { lat: geo.lat, lng: geo.lng } : null,
        currentLocation: { lat: curLoc.latitude, lng: curLoc.longitude },
        radiusMeters: geo?.radius || 500,
        biometricData: faceDescriptorToSubmit,
        siteMatched: siteMatched,
        type: "face",
      });

      //  SUCCESS FEEDBACK & STATUS
      const now = new Date();
      const companyTz = worker?.companyTimezone || "Asia/Colombo";
      const dateStr = now.toLocaleDateString("en-CA", { timeZone: companyTz });
      const timeStr = now.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: companyTz,
      });
      const serverStatus =
        apiResp?.record?.status || apiResp?.status || "on-time";
      const actionText = isCheckedIn ? "Checked Out" : "Checked In";

      // Determine final status text
      const matchText = siteMatched ? "MATCHED" : "MISMATCHED";

      setAuthProgress(100);
      setAuthStatus(`${actionText} [${matchText}] at ${timeStr}`);
      setAuthSuccess(true);

      toast({
        title: siteMatched
          ? "Attendance Matched"
          : "Attendance Recorded (Mismatched)",
        description: `Location: ${matchText} | Time: ${timeStr} | Status: ${serverStatus.toUpperCase()}`,
        variant: siteMatched ? "success" : "warning",
      });

      // Update Recent Activity locally so it shows before reload
      const newActivityItem = {
        id: Date.now(),
        type: isCheckedIn ? "check-out" : "check-in",
        project: site?.projectNumber || worker.currentProject,
        time: timeStr,
        date: dateStr,
        location: site?.name || "Site",
        method: "GPS",
        status: serverStatus,
        siteMatched: siteMatched,
      };
      setRecentActivity((prev) => [newActivityItem, ...prev]);

      setIsCheckedIn(!isCheckedIn);
    } catch (e: any) {
      setAuthSuccess(false);
      setAuthStatus(e?.message || "Validation failed.");
      toast({
        title: "Action Failed",
        description: e.message,
        variant: "destructive",
      });
    }
  };

  const formatDuration = (start: Date | null) => {
    if (!start) return "00:00:00";
    const diff = Math.floor((Date.now() - start.getTime()) / 1000);
    const hh = String(Math.floor(diff / 3600)).padStart(2, "0");
    const mm = String(Math.floor((diff % 3600) / 60)).padStart(2, "0");
    const ss = String(diff % 60).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  };

  const selectedSite = useMemo(
    () =>
      companySites.find(
        (s: any) => String(s._id ?? s.id) === selectedSiteId,
      ) as SiteLike | undefined,
    [companySites, selectedSiteId],
  );

  // UI
  return (
    <div
      className={`min-h-screen ${
        theme === "dark" ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"
      } flex flex-col items-center`}
    >
      <div className="py-4 text-center w-full bg-primary text-primary-foreground flex justify-between items-center px-4">
        <div className="flex-1" />
        <div className="flex-1 text-center">
          <h1 className="text-lg font-medium">Worker Portal</h1>
          {companyName && <p className="text-2xl font-bold">{companyName}</p>}
        </div>
        <div className="flex-1 flex justify-end">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            className="text-primary-foreground"
          >
            {theme === "light" ? (
              <Moon className="h-5 w-5" />
            ) : (
              <Sun className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      <div className="container max-w-md mx-auto px-4 py-8 flex-1">
        {!isLoggedIn ? (
          <>
            <Card
              className={
                theme === "dark" ? "bg-gray-800 text-white border-gray-700" : ""
              }
            >
              <CardHeader>
                <CardTitle>Worker Login</CardTitle>
                <CardDescription
                  className={theme === "dark" ? "text-gray-300" : ""}
                >
                  Enter your <b>Employee ID</b> and <b>Login ID</b>.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="employee-id"
                      className={theme === "dark" ? "text-gray-200" : ""}
                    >
                      Employee ID
                    </Label>
                    <Input
                      id="employee-id"
                      placeholder="e.g. EMP-002"
                      value={employeeNumber}
                      onChange={(e) => setEmployeeNumber(e.target.value)}
                      className={
                        theme === "dark"
                          ? "bg-gray-700 border-gray-600 text-white"
                          : ""
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        employeeNumber &&
                        password &&
                        handleLogin()
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="password"
                      className={theme === "dark" ? "text-gray-200" : ""}
                    >
                      Password (Login ID)
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="e.g. OSX77EMP-002"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={
                        theme === "dark"
                          ? "bg-gray-700 border-gray-600 text-white"
                          : ""
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        employeeNumber &&
                        password &&
                        handleLogin()
                      }
                    />
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  onClick={handleLogin}
                  disabled={isLoading || !employeeNumber || !password}
                >
                  {isLoading ? "Logging in..." : "Login"}
                </Button>
              </CardFooter>
            </Card>

            <Alert
              className={`mt-4 ${
                theme === "dark"
                  ? "bg-blue-900 border-blue-800 text-blue-100"
                  : "bg-blue-50 border-blue-200"
              }`}
            >
              <Info
                className={`h-4 w-4 ${
                  theme === "dark" ? "text-blue-300" : "text-blue-500"
                }`}
              />
              <AlertDescription
                className={theme === "dark" ? "text-blue-100" : "text-blue-700"}
              >
                <strong>Example:</strong>
                <br />
                Employee ID: <code>EMP-002</code>
                <br />
                Password (Login ID): <code>OSX77EMP-002</code>
              </AlertDescription>
            </Alert>
          </>
        ) : (
          <div className="space-y-6">
            <Card
              className={
                theme === "dark" ? "bg-gray-800 text-white border-gray-700" : ""
              }
            >
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>
                      {worker.fullName || worker.employeeNumber}
                    </CardTitle>
                    <CardDescription
                      className={theme === "dark" ? "text-gray-300" : ""}
                    >
                      Employee ID: {worker.employeeNumber}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      // Forcefully stop all cameras *before* clearing state
                      stopCamera();

                      // Wait 200ms for cleanup before React unmounts
                      await new Promise((r) => setTimeout(r, 200));

                      // Clear all session data
                      setIsLoggedIn(false);
                      setWorker(null);
                      setRecentActivity([]);
                      setIsCheckedIn(false);
                      setCheckInTime(null);
                      setSelectedSiteId("");
                      setCompanySites([]);
                      clearSession();

                      toast({
                        title: "Logged out",
                        description: "You have been logged out successfully.",
                        variant: "success",
                      });
                    }}
                    className={
                      theme === "dark"
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : ""
                    }
                  >
                    Logout
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Briefcase className="h-4 w-4 mr-2" />
                    <span>
                      Current Project :{" "}
                      {
                        // Look for the project number in the selected site first
                        companySites.find(
                          (s) => String(s._id ?? s.id) === selectedSiteId,
                        )?.projectNumber ||
                          worker.currentProject ||
                          "-"
                      }
                    </span>
                  </div>

                  <div className="space-y-2">
                    <Label className={theme === "dark" ? "text-gray-200" : ""}>
                      Select Site
                    </Label>
                    <select
                      value={selectedSiteId}
                      onChange={(e) => {
                        setSelectedSiteId(e.target.value);
                        localStorage.setItem(
                          LS_KEYS.selectedSiteId,
                          e.target.value,
                        );
                      }}
                      className={`w-full rounded-md border px-3 py-2 ${
                        theme === "dark"
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300"
                      }`}
                    >
                      <option value="" disabled={sitesLoading}>
                        {sitesLoading
                          ? "Loading sites..."
                          : "-- Choose your site --"}
                      </option>
                      {companySites.map((s: any) => (
                        <option
                          key={String(s._id ?? s.id)}
                          value={String(s._id ?? s.id)}
                        >
                          {s.name} • {s.projectNumber}
                        </option>
                      ))}
                    </select>

                    <div className="text-xs mt-1 opacity-70">
                      <div>
                        GPS:{" "}
                        {location
                          ? `${location.latitude.toFixed(
                              6,
                            )}, ${location.longitude.toFixed(6)}`
                          : "no fix"}
                      </div>
                      <div>
                        Site:{" "}
                        {(() => {
                          const sel = companySites.find(
                            (s: any) =>
                              String(s._id ?? s.id) === selectedSiteId,
                          ) as SiteLike | undefined;
                          if (!sel) return "invalid or unset";
                          const g = getSiteGeo(sel);
                          return g
                            ? `${g.lat.toFixed(6)}, ${g.lng.toFixed(6)} • r=${
                                g.radius
                              }m`
                            : "invalid or unset";
                        })()}
                      </div>
                    </div>

                    {!sitesLoading && companySites.length === 0 && (
                      <div
                        className={`p-3 rounded-md text-sm ${
                          theme === "dark"
                            ? "bg-yellow-900 text-yellow-200"
                            : "bg-yellow-50 text-yellow-800"
                        }`}
                      >
                        <AlertCircle className="h-4 w-4 mr-2 inline" />
                        No sites found for your company.
                      </div>
                    )}

                    {locationError && (
                      <div
                        className={`p-3 rounded-md flex items-center text-sm ${
                          theme === "dark"
                            ? "bg-yellow-900 text-yellow-200"
                            : "bg-yellow-50 text-yellow-800"
                        }`}
                      >
                        <AlertCircle className="h-4 w-4 mr-2" />
                        {locationError}
                      </div>
                    )}
                  </div>

                  {isCheckedIn ? (
                    <div
                      className={`p-4 rounded-lg border ${
                        theme === "dark"
                          ? "bg-primary/20 border-primary/30"
                          : "bg-primary/10 border-primary/30"
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="h-3 w-3 rounded-full bg-primary mr-2 animate-pulse" />
                        <h3 className={`font-medium`}>Currently Checked In</h3>
                      </div>
                      <div className="mt-2 space-y-2 text-foreground">
                        <div className={`flex items-center text-sm`}>
                          <Briefcase className="h-4 w-4 mr-2" />
                          <span>
                            Project:{" "}
                            {companySites.find(
                              (s) => String(s._id ?? s.id) === selectedSiteId,
                            )?.projectNumber ||
                              worker.currentProject ||
                              "-"}
                          </span>
                        </div>
                        <div className={`flex items-center text-sm`}>
                          <Clock className="h-4 w-4 mr-2" />
                          <span>Duration: {formatDuration(checkInTime)}</span>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </CardContent>
              <CardFooter>
                <Button
                  className={`w-full ${
                    isCheckedIn
                      ? "bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                      : "bg-primary hover:bg-primary/90 text-primary-foreground"
                  }`}
                  onClick={openValidationDialog}
                  disabled={
                    sitesLoading ||
                    (!selectedSiteId && companySites.length === 0)
                  }
                >
                  {isCheckedIn ? (
                    <LogOut className="h-5 w-5 mr-2" />
                  ) : (
                    <LogIn className="h-5 w-5 mr-2" />
                  )}
                  {isCheckedIn ? "Check Out" : "Check In"}
                </Button>
              </CardFooter>
            </Card>

            <Card
              className={
                theme === "dark" ? "bg-gray-800 text-white border-gray-700" : ""
              }
            >
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription
                  className={theme === "dark" ? "text-gray-300" : ""}
                >
                  Your recent check-ins and check-outs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentActivity.length > 0 ? (
                    recentActivity.map((a: any, i: number) => (
                      <div
                        key={a.id || i}
                        className={`p-3 border rounded-md ${
                          a.type === "check-in"
                            ? "border-primary/30 bg-primary/10"
                            : "border-destructive/30 bg-destructive/10"
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex items-center">
                            {a.type === "check-in" ? (
                              <LogIn className="h-5 w-5 mr-2 text-primary" />
                            ) : (
                              <LogOut className="h-5 w-5 mr-2 text-destructive" />
                            )}
                            <div>
                              <h4 className="font-medium">
                                {a.type === "check-in"
                                  ? "Checked In"
                                  : "Checked Out"}
                              </h4>
                              <p className={`text-sm text-muted-foreground`}>
                                {a.project}
                                {a.method && ` (${a.method})`}
                              </p>
                            </div>
                          </div>
                          <div
                            className={`text-right text-sm text-muted-foreground`}
                          >
                            <div className="flex items-center justify-end">
                              <Clock className="h-3 w-3 mr-1" />
                              <span>{a.time}</span>
                            </div>
                            <div className="flex items-center justify-end mt-1">
                              <Calendar className="h-3 w-3 mr-1" />
                              <span>{a.date}</span>
                            </div>
                          </div>
                        </div>
                        <div
                          className={`mt-2 text-sm flex items-center text-muted-foreground`}
                        >
                          <MapPin className="h-3 w-3 mr-1" />
                          <span>{a.location}</span>
                        </div>
                        {a.status && (
                          <div className="flex items-center mt-2 text-xs font-medium">
                            {a.status === "on-time" && (
                              <CheckCircle className="h-4 w-4 mr-1 text-green-500" />
                            )}
                            {a.status === "late" && (
                              <Clock4 className="h-4 w-4 mr-1 text-yellow-500" />
                            )}
                            {a.status === "absent" && (
                              <XCircle className="h-4 w-4 mr-1 text-red-500" />
                            )}
                            {a.status === "left-early" && (
                              <AlertTriangle className="h-4 w-4 mr-1 text-orange-500" />
                            )}
                            <span
                              className={`${
                                a.status === "on-time"
                                  ? "text-green-600 dark:text-green-400"
                                  : a.status === "late"
                                    ? "text-yellow-600 dark:text-yellow-400"
                                    : a.status === "absent"
                                      ? "text-red-600 dark:text-red-400"
                                      : "text-gray-500 dark:text-gray-400"
                              }`}
                            >
                              Status: {a.status.replace("-", " ").toUpperCase()}
                            </span>
                            {a.siteMatched === false && (
                              <span className="ml-2 px-1.5 py-0.5 bg-red-100 text-red-700 rounded-sm text-[10px] border border-red-200 flex items-center gap-1 animate-pulse">
                                <MapPin className="h-3 w-3" />
                                LOCATION MISMATCH
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className={`text-center py-6 text-muted-foreground`}>
                      No recent activity found
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <div
        className={`mt-8 text-center text-sm w-full ${
          theme === "dark" ? "text-gray-400" : "text-gray-500"
        } py-4 space-y-2`}
      >
        <p>
          © {new Date().getFullYear()} Checkin-Out-Pro GPS. All rights reserved 
        </p>

        <div className="flex items-center justify-center gap-2 pt-2 border-t border-gray-200/10 max-w-[200px] mx-auto">
          <span className="text-[10px] uppercase tracking-widest opacity-60">
            Developed by
          </span>
          <a
            href="https://www.algroton.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-transform hover:scale-105 active:scale-95"
          >
            <img
              src="/logo.png"
              alt="Algroton"
              className={`h-4 w-auto object-contain ${
                theme === "dark" ? "brightness-100" : "brightness-0 opacity-70"
              } hover:brightness-110`}
            />
          </a>
        </div>
      </div>
      {/* Validation Dialog */}
      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent
          className={
            theme === "dark" ? "bg-gray-800 text-white border-gray-700" : ""
          }
        >
          <DialogHeader>
            <DialogTitle>{isCheckedIn ? "Check Out" : "Check In"}</DialogTitle>
            <DialogDescription
              className={theme === "dark" ? "text-gray-300" : ""}
            >
              We will validate your selected site, GPS radius and allowed time
              window.
            </DialogDescription>
          </DialogHeader>

          {processingAuth ? (
            <div className="py-6 space-y-4">
              <Progress value={authProgress} className="w-full" />
              <p className="text-center">{authStatus}</p>
              {authSuccess !== null && (
                <div
                  className={`flex items-center justify-center p-4 rounded-lg ${
                    authSuccess
                      ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                      : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                  }`}
                >
                  {authSuccess ? (
                    <CheckCircle className="h-6 w-6 mr-2" />
                  ) : (
                    <X className="h-6 w-6 mr-2" />
                  )}
                  <span>{authSuccess ? "Success" : "Validation failed"}</span>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Biometrics UI reserved (not required) */}
              <Tabs defaultValue="face" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="face">Face Recognition</TabsTrigger>
                  <TabsTrigger value="fingerprint">Fingerprint</TabsTrigger>
                </TabsList>
                <TabsContent value="face" className="mt-4">
                  <div
                    className={`border rounded-lg p-6 flex flex-col items-center justify-center ${
                      theme === "dark"
                        ? "border-gray-700 bg-gray-900"
                        : "border-gray-200"
                    }`}
                  >
                    <video
                      ref={videoRef}
                      className="w-48 h-48 rounded-full bg-gray-200 dark:bg-gray-700 mb-4 object-cover"
                      muted
                      playsInline
                    />
                    <canvas
                      ref={canvasRef}
                      className="absolute top-0 left-0"
                      width={640}
                      height={480}
                    />
                    <p
                      className={`text-sm ${
                        theme === "dark" ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      Position your face (not required now)
                    </p>
                  </div>
                </TabsContent>
                <TabsContent value="fingerprint" className="mt-4">
                  <div
                    className={`border rounded-lg p-6 flex flex-col items-center justify-center ${
                      theme === "dark"
                        ? "border-gray-700 bg-gray-900"
                        : "border-gray-200"
                    }`}
                  >
                    <div
                      ref={fingerprintRef}
                      className="w-48 h-48 rounded-full bg-gray-200 dark:bg-gray-700 mb-4 flex items-center justify-center fingerprint-scanner"
                    >
                      <Fingerprint className="h-16 w-16 text-gray-400" />
                    </div>
                    <p
                      className={`text-sm ${
                        theme === "dark" ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      Fingerprint (not required now)
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}

          <DialogFooter>
            {processingAuth ? (
              <Button
                variant="outline"
                onClick={() => setShowAuthDialog(false)}
                disabled={authProgress < 100 && authSuccess === null}
              >
                {authSuccess !== null ? "Close" : "Cancel"}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowAuthDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleValidateAndRecord}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  disabled={!selectedSiteId}
                  aria-busy={processingAuth}
                >
                  {isCheckedIn ? "Validate & Check Out" : "Validate & Check In"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Biometric Setup Dialog */}
      <Dialog open={showBiometricSetup} onOpenChange={setShowBiometricSetup}>
        <DialogContent
          className={
            theme === "dark" ? "bg-gray-800 text-white border-gray-700" : ""
          }
        >
          <DialogHeader>
            <DialogTitle>Biometric Registration</DialogTitle>
            <DialogDescription
              className={theme === "dark" ? "text-gray-300" : ""}
            >
              Please capture your Face ID and Fingerprint. This is required only
              once.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-5">
            {/* Camera preview */}
            <div className="relative flex justify-center items-center">
              <Webcam
                ref={webcamRef}
                audio={false}
                className="rounded-lg"
                videoConstraints={{ facingMode: "user" }}
                style={{ width: 340, height: 150 }}
              />
              <canvas
                ref={canvasRef}
                className="absolute top-0 left-0"
                width={340}
                height={150}
                // width={640}
                // height={480}
              />
            </div>

            <Button
              onClick={async () => {
                try {
                  if (!webcamRef.current) throw new Error("Camera not ready");
                  const faceapi = await import("@vladmandic/face-api");
                  const video = webcamRef.current.video as HTMLVideoElement;
                  const options = new faceapi.TinyFaceDetectorOptions({
                    inputSize: 320,
                    scoreThreshold: 0.5,
                  });
                  const detection = await faceapi
                    .detectSingleFace(
                      video,
                      new faceapi.TinyFaceDetectorOptions(),
                    )
                    .withFaceLandmarks()
                    .withFaceDescriptor();

                  if (!detection) throw new Error("No face detected");
                  const descriptor = Array.from(detection.descriptor);
                  //setFaceData(JSON.stringify(descriptor));
                  setFaceData(descriptor);

                  toast({
                    title: "Face captured successfully",
                    description:
                      "Your face data has been encoded for recognition.",
                    variant: "success",
                  });
                } catch (err: any) {
                  toast({
                    title: "Camera Error",
                    description: err.message,
                    variant: "destructive",
                  });
                }
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Capture Face
            </Button>

            {faceData && (
              <p className="text-green-500 text-sm mt-2 font-medium"></p>
            )}

            <div className="flex flex-col items-center space-y-3">
              <div
                ref={fingerprintRef}
                className={`w-32 h-32 rounded-full border-4 flex items-center justify-center transition-all ${
                  isScanningFingerprint
                    ? "border-green-500 animate-pulse bg-green-100 dark:bg-green-900"
                    : "border-gray-400 bg-gray-200 dark:bg-gray-700"
                }`}
              >
                <Fingerprint
                  className={`h-16 w-16 ${
                    isScanningFingerprint
                      ? "text-green-600 dark:text-green-300"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                />
              </div>

              <Button
                disabled={isScanningFingerprint}
                onClick={() => {
                  setIsScanningFingerprint(true);
                  setFingerProgress(0);

                  const interval = setInterval(() => {
                    setFingerProgress((prev) => {
                      const next = prev + 10;
                      if (next >= 100) {
                        clearInterval(interval);
                        setTimeout(() => {
                          const data = "finger_" + new Date().getTime();
                          setFingerData(data);
                          setIsScanningFingerprint(false);
                          toast({
                            title: "Fingerprint captured successfully!",
                            description:
                              "Your fingerprint data has been stored securely.",
                            variant: "success",
                          });
                        }, 300);
                      }
                      return next;
                    });
                  }, 200);
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                {isScanningFingerprint ? "Scanning..." : "Capture Fingerprint"}
              </Button>

              {isScanningFingerprint && (
                <Progress value={fingerProgress} className="w-full" />
              )}

              {fingerData && (
                <p className="text-sm text-green-600 dark:text-green-400 font-medium"></p>
              )}
            </div>

            <Button
              onClick={async () => {
                try {
                  const token = localStorage.getItem(LS_KEYS.token) || "";
                  const res = await fetch(
                    `${API_BASE}/api/workers/${worker.id}/biometrics`,
                    {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                      },
                      body: JSON.stringify({
                        faceData,
                        fingerprintData: fingerData,
                      }),
                    },
                  );
                  if (!res.ok) throw new Error(await res.text());
                  toast({
                    title: "Biometric data saved successfully!",
                    variant: "success",
                  });
                  setShowBiometricSetup(false);
                } catch (err: any) {
                  toast({
                    title: "Biometric save failed",
                    description: err.message,
                    variant: "destructive",
                  });
                }
              }}
              disabled={!faceData && !fingerData}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              Save Biometric Data
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .fingerprint-scanner.scanning {
          animation: pulse 1.5s infinite;
        }
        .fingerprint-scanner.animate-pulse {
          animation: pulse 1.2s infinite;
        }
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
          }
        }
      `}</style>
    </div>
  );
}
